import React, { useState } from 'react';
import { usePersonas } from '../../hooks/usePersonas';
import { useRunEvaluation } from '../../hooks/useEvaluations';
import { FileUpload } from '../common/FileUpload';

interface EvaluationRunnerProps {
  projectId: string;
  onComplete: () => void;
  onCancel: () => void;
}

export const EvaluationRunner: React.FC<EvaluationRunnerProps> = ({
  projectId,
  onComplete,
  onCancel,
}) => {
  const { personas } = usePersonas(projectId);
  const { runEvaluation, loading, error } = useRunEvaluation();

  const [title, setTitle] = useState('');
  const [assetDescription, setAssetDescription] = useState('');
  const [assetUrl, setAssetUrl] = useState('');
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [assetSource, setAssetSource] = useState<'url' | 'upload'>('url');
  const [selectedPersonas, setSelectedPersonas] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !assetDescription.trim() || selectedPersonas.length === 0) {
      return;
    }

    // Use uploaded file URL if available, otherwise use the URL field
    const finalAssetUrl = uploadedFileUrl || assetUrl.trim() || undefined;

    try {
      await runEvaluation({
        projectId,
        title: title.trim(),
        assetDescription: assetDescription.trim(),
        assetUrl: finalAssetUrl,
        personaIds: selectedPersonas,
      });
      onComplete();
    } catch (err) {
      console.error('Failed to run evaluation:', err);
    }
  };

  const togglePersona = (personaId: string) => {
    setSelectedPersonas((prev) =>
      prev.includes(personaId)
        ? prev.filter((id) => id !== personaId)
        : [...prev, personaId]
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-8 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">New Evaluation</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., New Ad Campaign Evaluation"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          {/* Asset Description */}
          <div className="mb-4">
            <label htmlFor="assetDescription" className="block text-sm font-medium text-gray-700 mb-2">
              Asset Description
            </label>
            <textarea
              id="assetDescription"
              value={assetDescription}
              onChange={(e) => setAssetDescription(e.target.value)}
              placeholder="Describe the creative asset you want evaluated (ad copy, tagline, visual description, product concept, etc.)"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Provide detailed description including copy, visuals, messaging, or product features
            </p>
          </div>

          {/* Asset Source Tabs */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asset <span className="text-gray-400 font-normal">(optional)</span>
            </label>

            {/* Tab buttons */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                type="button"
                onClick={() => setAssetSource('url')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  assetSource === 'url'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Link URL
              </button>
              <button
                type="button"
                onClick={() => setAssetSource('upload')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  assetSource === 'upload'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Upload File
              </button>
            </div>

            {/* URL input */}
            {assetSource === 'url' && (
              <div>
                <input
                  type="url"
                  id="assetUrl"
                  value={assetUrl}
                  onChange={(e) => setAssetUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Link to image, video, or document (if hosted online)
                </p>
              </div>
            )}

            {/* File upload */}
            {assetSource === 'upload' && (
              <FileUpload
                projectId={projectId}
                onUploadComplete={(url, fileName, fileType) => {
                  setUploadedFileUrl(url);
                  setUploadedFileName(fileName);
                }}
                onUploadError={(error) => {
                  console.error('File upload error:', error);
                }}
                acceptedTypes="image/*,video/*,.pdf,.doc,.docx"
                maxSizeMB={10}
                label=""
                helperText="Upload creative asset (images, videos, PDFs, docs)"
              />
            )}
          </div>

          {/* Persona Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Personas ({selectedPersonas.length} selected)
            </label>
            <div className="border border-gray-300 rounded-md max-h-60 overflow-y-auto p-3 space-y-2">
              {personas.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No personas available. Generate personas first.
                </p>
              ) : (
                personas.map((persona) => (
                  <label
                    key={persona.personaId}
                    className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPersonas.includes(persona.personaId)}
                      onChange={() => togglePersona(persona.personaId)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{persona.name}</div>
                      <div className="text-xs text-gray-500">
                        {persona.demographics.age} • {persona.demographics.occupation}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error.message}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim() || !assetDescription.trim() || selectedPersonas.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Running Evaluation...' : 'Run Evaluation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
